import React, {ReactNode, useMemo} from 'react';

import {useTranslation} from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';

import {ReactComponent as ExportIcon} from 'assets/svg/download.svg';
import {colors} from 'theme';
import {formatNumber} from 'utils/number';

import {Icon} from '../Icon';
import {Spinner} from '../Spinner';

import {Filters, FiltersType} from './Filters';
import {Sorting, SortingType} from './Sorting';
import {
    ConfigBlock,
    EndMessage,
    ExportButton,
    HeaderControls,
    ListHeader,
    Placeholder,
    SpinnerLayer,
    TotalRowsCount,
} from './styled';
import {ConfigElement} from './types';

interface Props<T> {
    rows?: T[];
    totalRowsCount?: number;
    hasMore?: boolean;
    headerControls?: ReactNode;
    renderRow: (row: T) => ReactNode;
    onNextPage?: () => void;
    config: ConfigElement[];
    onFiltersChange?: (filters: FiltersType) => void;
    onSortingChange?: (sorting: SortingType) => void;
    className?: string;
    sorting?: SortingType;
    filters?: FiltersType;
    exportConfig?: {
        exportPending: boolean;
        callback: Function;
    };
    showConfigBlock?: boolean;
}

// Nested columns flatten
const flatConfig = (columns: ConfigElement[]): ConfigElement[] =>
    columns.reduce(
        (accum: ConfigElement[], elem: ConfigElement) =>
            accum.concat(
                elem.columns
                    ? flatConfig(elem.columns.map((item) => ({...item, parentHeader: elem.Header})))
                    : elem,
            ),
        [] as ConfigElement[],
    );

export const EndlessList: <T>(p: Props<T>) => React.ReactElement = ({
    rows = [],
    totalRowsCount,
    hasMore,
    headerControls,
    renderRow,
    onNextPage,
    config,
    sorting,
    filters,
    onFiltersChange,
    onSortingChange,
    className,
    exportConfig,
    showConfigBlock = true,
}) => {
    const {t} = useTranslation();

    const sortingConfig = useMemo(() => flatConfig(config).filter((elem) => !elem.disableSortBy), [
        config,
    ]);
    const filtersConfig = useMemo(() => flatConfig(config).filter((elem) => !!elem.Filter), [
        config,
    ]);

    const showSorting = sorting && sortingConfig.length > 0;
    const showFilters = filters && filtersConfig.length > 0;

    const handleFiltersChange = (filters: FiltersType) => {
        if (!onFiltersChange) {
            return;
        }

        onFiltersChange(filters);
    };

    const handleSortingChange = (sorting: SortingType) => {
        if (!onSortingChange) {
            return;
        }

        onSortingChange(sorting);
    };

    const innerHasMore =
        (totalRowsCount !== undefined && rows.length < totalRowsCount) ||
        (hasMore !== undefined && hasMore);

    return (
        <div className={className}>
            {headerControls && <HeaderControls>{headerControls}</HeaderControls>}
            {(showSorting || showFilters) && showConfigBlock && (
                <ConfigBlock>
                    {showSorting && (
                        <Sorting
                            config={sortingConfig}
                            onChange={handleSortingChange}
                            defaultValue={sorting}
                        />
                    )}
                    {showFilters && filters && (
                        <Filters
                            config={filtersConfig}
                            filters={filters}
                            onChange={handleFiltersChange}
                        />
                    )}
                </ConfigBlock>
            )}

            {(totalRowsCount || exportConfig) && (
                <ListHeader alignRight={totalRowsCount == null}>
                    {totalRowsCount != null && (
                        <TotalRowsCount>
                            {t('components.endlessList.totalRecords')}:{' '}
                            {formatNumber(totalRowsCount)}
                        </TotalRowsCount>
                    )}
                    {exportConfig != null && (
                        <ExportButton
                            onClick={() => exportConfig.callback()}
                            disabled={exportConfig.exportPending}
                        >
                            <Icon
                                source={ExportIcon}
                                width={20}
                                height={20}
                                fillColor={colors.brandPrimary}
                            />
                            {totalRowsCount == null && t('components.endlessList.export')}
                        </ExportButton>
                    )}
                </ListHeader>
            )}

            <InfiniteScroll
                dataLength={rows.length}
                next={onNextPage || (() => {})}
                hasMore={innerHasMore}
                loader={
                    <SpinnerLayer>
                        <Spinner />
                    </SpinnerLayer>
                }
                endMessage={
                    rows.length > 0 && (
                        <EndMessage>{t('components.endlessList.endMessage')}</EndMessage>
                    )
                }
            >
                {rows.length > 0 &&
                    rows.map((elem, index) => (
                        <React.Fragment key={index}>{renderRow(elem)}</React.Fragment>
                    ))}
                {rows.length === 0 && (
                    <Placeholder>{t('components.endlessList.noData')}</Placeholder>
                )}
            </InfiniteScroll>
        </div>
    );
};
